import {useCallback, useEffect, useState} from "react";
import {Link} from "react-router-dom";
import baseUrl from "../../../baseUrl";
import {requester} from "../../../services/requester";
import {useQuestions} from "../../../contexts/QuestionsContext";
import {useConfirm} from "../../../contexts/ConfirmContext";

// И2 — раздел „Въпроси": всички въпроси от потребителите, отговор на място и
// контактите на автора (за случаите, в които е по-лесно да се обади).

const STATUS_LABELS = {
    new: {text: "нов", cls: "bg-secondary"},
    in_progress: {text: "в работа", cls: "bg-warning text-dark"},
    answered: {text: "отговорен", cls: "bg-success"},
};

const FILTERS = [
    {value: 'new', label: 'Нови'},
    {value: 'in_progress', label: 'В работа'},
    {value: 'answered', label: 'Отговорени'},
    {value: '', label: 'Всички'},
];

const Contacts = ({contacts, userId}) => {
    const has = contacts && (contacts.full_name || contacts.email || contacts.phone || contacts.note);

    if (!has) {
        return (
            <div className="mt-2 small text-muted">
                Няма въведени контактни данни.{' '}
                <Link to="/admin/users">Попълни ги в „Потребители"</Link>
                {userId ? '' : ' (потребителят е изтрит)'}
            </div>
        );
    }

    return (
        <div className="mt-2 p-2 rounded small" style={{background: "var(--app-surface-2)"}}>
            {contacts.full_name && <div><strong>{contacts.full_name}</strong></div>}
            {contacts.email && <div>Имейл: <a href={`mailto:${contacts.email}`}>{contacts.email}</a></div>}
            {contacts.phone && <div>Телефон: <a href={`tel:${contacts.phone}`}>{contacts.phone}</a></div>}
            {contacts.note && <div className="text-muted mt-1">{contacts.note}</div>}
        </div>
    );
};

const QuestionCard = ({q, onChanged}) => {
    const request = requester();
    const confirm = useConfirm();
    const [showContacts, setShowContacts] = useState(false);
    const [answering, setAnswering] = useState(false);
    const [answer, setAnswer] = useState(q.answer || '');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const status = STATUS_LABELS[q.status] || STATUS_LABELS.new;

    const send = (body) => {
        setBusy(true);
        setError('');
        request.put(`${baseUrl()}/admin/questions/${q.id}`, body)
            .then(() => {
                setAnswering(false);
                onChanged();
            })
            .catch(err => setError(err.message || 'Грешка при запис.'))
            .finally(() => setBusy(false));
    };

    const remove = async () => {
        if (!await confirm({
            title: 'Да се изтрие ли въпросът?',
            message: 'Въпросът и отговорът към него изчезват и за потребителя.',
        })) return;
        setBusy(true);
        request.delete(`${baseUrl()}/admin/questions/${q.id}`)
            .then(onChanged)
            .catch(err => setError(err.message || 'Грешка при изтриване.'))
            .finally(() => setBusy(false));
    };

    return (
        <div className="card mb-3">
            <div className="card-body">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div className="small text-muted">
                        <span className={`badge ${status.cls} me-2`}>{status.text}</span>
                        <strong>{q.username}</strong> · {q.create_on}
                        {q.context_url && <> · <code>{q.context_url}</code></>}
                    </div>
                    <div className="d-flex gap-2">
                        <button className="btn btn-sm btn-outline-secondary"
                                onClick={() => setShowContacts(v => !v)}>
                            Контакти
                        </button>
                        <button className="btn btn-sm btn-outline-primary"
                                onClick={() => setAnswering(v => !v)}>
                            {q.answer ? 'Редактирай отговора' : 'Отговори'}
                        </button>
                        <button className="btn btn-sm btn-outline-danger" onClick={remove} disabled={busy}>
                            Изтрий
                        </button>
                    </div>
                </div>

                <div className="mt-2" style={{whiteSpace: "pre-wrap"}}>{q.text}</div>

                {showContacts && <Contacts contacts={q.contacts} userId={q.user_id}/>}

                {q.answer && !answering && (
                    <div className="mt-2 p-2 rounded" style={{background: "var(--app-surface-2)"}}>
                        <div className="fw-bold">Отговор:</div>
                        <div style={{whiteSpace: "pre-wrap"}}>{q.answer}</div>
                        <small className="text-muted">{q.answered_by} · {q.answered_on}</small>
                    </div>
                )}

                {error && <div className="alert alert-danger py-2 mt-2 mb-0">{error}</div>}

                {answering && (
                    <div className="mt-3">
                        <textarea className="form-control" rows={4} maxLength={4000}
                                  placeholder="Отговорът се показва на потребителя в раздел Помощ."
                                  value={answer} onChange={e => setAnswer(e.target.value)}/>
                        <div className="mt-2 d-flex gap-2 flex-wrap">
                            <button className="btn btn-primary btn-sm" disabled={busy || !answer.trim()}
                                    onClick={() => send({answer: answer.trim()})}>
                                {busy ? 'Запис…' : 'Запиши отговора'}
                            </button>
                            {q.status !== 'in_progress' && (
                                <button className="btn btn-outline-secondary btn-sm" disabled={busy}
                                        onClick={() => send({status: 'in_progress'})}>
                                    Отбележи като „в работа"
                                </button>
                            )}
                            <button className="btn btn-outline-secondary btn-sm"
                                    onClick={() => setAnswering(false)}>
                                Отказ
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export const AdminQuestions = () => {
    const request = requester();
    const {refresh: refreshCount} = useQuestions();

    // По подразбиране показваме „Нови" — това е, което иска действие.
    const [status, setStatus] = useState('new');
    const [questions, setQuestions] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        setLoading(true);
        setError('');
        const url = status
            ? `${baseUrl()}/admin/questions?status=${status}`
            : `${baseUrl()}/admin/questions`;
        request.get(url)
            .then(setQuestions)
            .catch(err => setError(err.message || 'Грешка при зареждане на въпросите.'))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    useEffect(() => {
        load();
    }, [load]);

    const onChanged = () => {
        load();
        refreshCount();   // значката в лентата да отрази промяната веднага
    };

    return (
        <div>
            <div className="d-flex align-items-center mb-3 flex-wrap gap-2">
                <h3 className="me-auto mb-0">Въпроси</h3>
                <div className="btn-group">
                    {FILTERS.map(f => (
                        <button key={f.value} type="button"
                                className={`btn btn-sm ${status === f.value ? 'btn-secondary' : 'btn-outline-secondary'}`}
                                onClick={() => setStatus(f.value)}>
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            {!loading && !questions.length && (
                <p className="text-muted">
                    {status === 'new' ? 'Няма нови въпроси.' : 'Няма въпроси в тази категория.'}
                </p>
            )}

            {questions.map(q => <QuestionCard key={q.id} q={q} onChanged={onChanged}/>)}
        </div>
    );
};
